import { HistoryLog } from "~/services/api/types/user";
import { DISABLED_STATES, stateMap, STATES } from "~/constants/states";

export const IsStateEligible = (state: string | undefined): boolean => {
    if (!state) return false;

    if (STATES.includes(state)) {
        return !DISABLED_STATES.includes(state);
    }

    const stateKey = Object.keys(stateMap).find(
        (key) => stateMap[key] === state
    );

    return stateKey ? !DISABLED_STATES.includes(stateKey) : false;
};

/**
 * Checks if a quiz with the specified category has been completed by the user.
 * 
 * Searches through the user's history logs to find any completed survey that matches
 * the given category. A quiz is considered completed if there's a log entry with
 * action "SURVEY_COMPLETED" and a matching surveyCategory.
 * 
 * @param history - Array of history logs to search through. Each log represents
 *                  a user action or event in the system.
 * @param category - The survey category to check for completion. Must match the
 *                  `surveyCategory` in the log's `logData`. Defaults to empty string.
 * 
 * @returns `true` if a completed survey with the matching category is found,
 *          `false` otherwise (including when history is empty).
 * 
 * @example
 * ```typescript
 * const history = [
 *   {
 *     action: "SURVEY_COMPLETED",
 *     logData: { surveyCategory: "endometriosis", surveyId: "123" }
 *   }
 * ];
 * 
 * isQuizCompleted(history, "endometriosis"); // Returns true
 * isQuizCompleted(history, "skin-care");     // Returns false
 * isQuizCompleted([], "endometriosis");       // Returns false
 * ```
 */
export const isQuizCompleted = (history: HistoryLog[], category = "") => {
    if (!history.length) return false;
    
    return history.some(
        (log) =>
            log.action === "SURVEY_COMPLETED" &&
            log.logData.surveyCategory === category
    );
};
